import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface EmployeeData {
  id: number;
  name: string;
  daily_weights: { [date: string]: number };
  total_weight: number;
}

interface MetricsResponse {
  start_date: string;
  end_date: string;
  employees: EmployeeData[];
}

type DateRange = 'week' | 'month' | 'custom';

// Generate colors for different employees
const COLORS = [
  '#2196F3', '#F44336', '#4CAF50', '#FF9800', '#9C27B0',
  '#00BCD4', '#E91E63', '#8BC34A', '#FF5722', '#673AB7',
  '#009688', '#FFC107', '#795548', '#607D8B', '#3F51B5',
];

const EmployeeMetrics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricsData, setMetricsData] = useState<MetricsResponse | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('week');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);

  const fetchMetrics = async (startDate: string, endDate: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/metrics/employee?start_date=${startDate}&end_date=${endDate}`,
        {
          headers: {
            Authorization: token || '',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch employee metrics data');
      }

      const data = await response.json();
      setMetricsData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const calculateDateRange = (range: DateRange): { start: string; end: string } => {
    const today = new Date();
    const end = today.toISOString().split('T')[0];
    let start: Date;

    switch (range) {
      case 'week':
        start = new Date(today);
        start.setDate(today.getDate() - 7);
        break;
      case 'month':
        start = new Date(today);
        start.setMonth(today.getMonth() - 1);
        break;
      case 'custom':
        return { start: customStartDate, end: customEndDate };
      default:
        start = new Date(today);
        start.setDate(today.getDate() - 7);
    }

    return { start: start.toISOString().split('T')[0], end };
  };

  useEffect(() => {
    if (dateRange !== 'custom' || (customStartDate && customEndDate)) {
      const { start, end } = calculateDateRange(dateRange);
      if (start && end) {
        fetchMetrics(start, end);
      }
    }
  }, [dateRange, customStartDate, customEndDate]);

  const handleDateRangeChange = (event: SelectChangeEvent<DateRange>) => {
    setDateRange(event.target.value as DateRange);
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      fetchMetrics(customStartDate, customEndDate);
    }
  };

  const prepareChartData = () => {
    if (!metricsData || !metricsData.employees.length) {
      return [];
    }

    // If a specific employee is selected, show only their data
    if (selectedEmployeeId !== null) {
      const selectedEmployee = metricsData.employees.find(emp => emp.id === selectedEmployeeId);
      if (!selectedEmployee) {
        return [];
      }

      const sortedDates = Object.keys(selectedEmployee.daily_weights).sort();
      return sortedDates.map(date => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weight: selectedEmployee.daily_weights[date],
        fullDate: date,
      }));
    }

    // No employee selected - show all employees grouped by date
    const allDates = new Set<string>();
    metricsData.employees.forEach(emp => {
      Object.keys(emp.daily_weights).forEach(date => allDates.add(date));
    });

    const sortedDates = Array.from(allDates).sort();

    // Prepare chart data with each employee as a separate key
    return sortedDates.map(date => {
      const dataPoint: any = { 
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: date,
      };
      
      metricsData.employees.forEach(emp => {
        if (emp.daily_weights[date]) {
          dataPoint[emp.name] = emp.daily_weights[date];
        }
      });
      
      return dataPoint;
    });
  };

  const getSelectedEmployee = () => {
    if (!metricsData || selectedEmployeeId === null) return null;
    return metricsData.employees.find(emp => emp.id === selectedEmployeeId);
  };

  const getEmployeeColor = (employeeId: number) => {
    if (!metricsData) return COLORS[0];
    const index = metricsData.employees.findIndex(emp => emp.id === employeeId);
    return COLORS[index % COLORS.length];
  };

  const formatYAxis = (value: number) => {
    return `${value.toFixed(0)} kg`;
  };

  const chartData = prepareChartData();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Employee Metrics
      </Typography>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={dateRange}
              label="Time Period"
              onChange={handleDateRangeChange}
            >
              <MenuItem value="week">Last Week</MenuItem>
              <MenuItem value="month">Last Month</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>

          {dateRange === 'custom' && (
            <>
              <TextField
                label="Start Date"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="contained"
                onClick={handleCustomDateApply}
                disabled={!customStartDate || !customEndDate}
              >
                Apply
              </Button>
            </>
          )}

          {metricsData && (
            <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary' }}>
              {new Date(metricsData.start_date).toLocaleDateString()} - {new Date(metricsData.end_date).toLocaleDateString()}
            </Typography>
          )}
        </Box>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && metricsData && (
        <>
          {/* Employee Selection Table */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Employee Production Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click on an employee to filter the chart below, or view all employees at once
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Employee Name</strong></TableCell>
                    <TableCell align="right"><strong>Total Weight (kg)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metricsData.employees.length > 0 ? (
                    [...metricsData.employees]
                      .sort((a, b) => b.total_weight - a.total_weight)
                      .map((emp, index) => (
                        <TableRow 
                          key={emp.id}
                          onClick={() => setSelectedEmployeeId(selectedEmployeeId === emp.id ? null : emp.id)}
                          sx={{ 
                            cursor: 'pointer',
                            backgroundColor: selectedEmployeeId === emp.id 
                              ? `${COLORS[metricsData.employees.findIndex(e => e.id === emp.id) % COLORS.length]}20`
                              : 'transparent',
                            '&:hover': { 
                              backgroundColor: selectedEmployeeId === emp.id 
                                ? `${COLORS[metricsData.employees.findIndex(e => e.id === emp.id) % COLORS.length]}30`
                                : 'action.hover'
                            },
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: COLORS[metricsData.employees.findIndex(e => e.id === emp.id) % COLORS.length],
                                }}
                              />
                              {emp.name}
                              {selectedEmployeeId === emp.id && (
                                <Chip 
                                  label="Selected" 
                                  size="small" 
                                  color="primary"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{emp.total_weight.toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No employee data available for this period
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Bar Chart */}
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              {selectedEmployeeId !== null && getSelectedEmployee() 
                ? `Daily Production: ${getSelectedEmployee()?.name}`
                : 'Daily Production: All Employees'}
            </Typography>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tickFormatter={formatYAxis} />
                  <Tooltip 
                    formatter={(value: number, name: string) => [`${value.toFixed(2)} kg`, name]}
                    labelFormatter={(label) => `Date: ${label}`}
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                    }}
                  />
                  {selectedEmployeeId === null ? (
                    <>
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      {metricsData.employees.map((emp, index) => (
                        <Bar
                          key={emp.id}
                          dataKey={emp.name}
                          fill={COLORS[index % COLORS.length]}
                          radius={[4, 4, 0, 0]}
                          maxBarSize={40}
                        />
                      ))}
                    </>
                  ) : (
                    <Bar 
                      dataKey="weight" 
                      name={getSelectedEmployee()?.name || 'Weight'}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={60}
                    >
                      {chartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={getEmployeeColor(selectedEmployeeId)}
                        />
                      ))}
                    </Bar>
                  )}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No production data available for this period
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default EmployeeMetrics;

